import { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Collapse,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Drawer as MuiDrawer,
  styled,
  Typography,
  IconButton,
  Divider,
  Alert,
} from '@mui/material';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  GetTrainingModule,
  GetProductTrainingStatus,
  SetProductTraining,
  CheckProductTrainingAccess,
} from '../../api/trainingModule';
import { TrainingModuleVideoPlayer } from './components/TrainingModuleVideoPlayer';
import { convertIsoToReadableDate } from '../../components/Utilities/HelperMethods';
import MenuIcon from '@mui/icons-material/Menu';
import useMediaQuery from '@mui/material/useMediaQuery';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import CheckIcon from '@mui/icons-material/Check';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { LoadingButton } from '../../components/Buttons/LoadingButton';
import { useVideoModulePlaylist } from '../../hooks/util/useVideoModulePlaylist';
import TrainingModuleLoadingState from './components/TrainingModuleLoadingState';
import { SidebarButton } from './components/SidebarButton';
import { Snackbar } from '../../components/Global/Snackbar/Snackbar';

const centeredMessageSx = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  gap: 2,
  padding: 4,
  minHeight: '50vh',
};

const TrainingModuleError = ({ onRetry }) => (
  <Box sx={centeredMessageSx}>
    <Alert severity="error">Something went wrong loading this training.</Alert>
    <Button variant="contained" onClick={onRetry}>
      Retry
    </Button>
  </Box>
);

export const TrainingModule = () => {
  const queryClient = useQueryClient();
  const { trainingModule } = useParams();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [videosCompleted, setVideosCompleted] = useState([]);
  const [modulesCount, setModulesCount] = useState(0);
  const [modulesCompletedCount, setModulesCompletedCount] = useState(0);
  const [modules, setModules] = useState([]);
  const isMedium = useMediaQuery('(max-width:1200px)');
  const isMobile = useMediaQuery('(max-width:600px)');
  const [snackbarDetails, setSnackbarDetails] = useState({
    open: false,
    status: '',
    message: '',
  });

  const Drawer = styled(MuiDrawer, { drawerOpen })({
    position: 'relative',
    width: '15%',
    display: !isMedium ? 'block' : 'none',
    '& .MuiDrawer-paper': {
      width: '100%',
      position: 'absolute',
    },
  });

  const MobileDrawer = styled(Collapse)(({ theme }) => ({
    display: isMedium || isMobile ? 'block' : 'none',
    position: 'absolute',
    width: isMobile ? '100%' : '35%',
    height: '100%',
    zIndex: '500',
    background: theme.palette.common.white,
    borderRight: `1px solid`,
    borderColor: theme.palette.grey[300],
  }));

  const {
    data: hasProductAccess,
    error: hasProductAccessErrorDetail,
    isPending: hasProductAccessLoading,
    isError: hasProductAccessError,
    refetch: refetchAccess,
  } = useQuery({
    queryKey: ['checkProductTrainingAccess', trainingModule],
    queryFn: () => CheckProductTrainingAccess(trainingModule)(),
    // A 401 means the user lacks access; retrying won't change that.
    retry: (failureCount, error) => error?.status !== 401 && failureCount < 3,
  });

  const accessDenied =
    (hasProductAccessError && hasProductAccessErrorDetail?.status === 401) ||
    (!hasProductAccessLoading && !hasProductAccessError && !hasProductAccess);

  const {
    data: productTrainingStatus,
    isPending: getProductTrainingStatusLoading,
    isError: getProductTrainingStatusError,
    isSuccess: getProductTrainingStatusSuccess,
    refetch: refetchStatus,
  } = useQuery({
    queryKey: ['getProductTrainingStatus', trainingModule],
    queryFn: () => GetProductTrainingStatus(trainingModule)(),
    enabled: !!hasProductAccess,
  });

  const {
    data: trainingModuleData,
    isPending: getTrainingModuleLoading,
    isSuccess: getTrainingModuleSuccess,
    isError: getTrainingModuleError,
    refetch: refetchModule,
  } = useQuery({
    queryKey: ['getTrainingModule', trainingModule],
    queryFn: () => GetTrainingModule(trainingModule)(),
    enabled: !!hasProductAccess,
  });

  useEffect(() => {
    if (getTrainingModuleSuccess && trainingModuleData) {
      const parsedVideos = JSON.parse(trainingModuleData[0].videos);
      setModulesCount(parsedVideos.length);
      setModules(parsedVideos);
    }
  }, [getTrainingModuleSuccess, trainingModuleData]);

  useEffect(() => {
    if (getTrainingModuleSuccess && getProductTrainingStatusSuccess) {
      if (productTrainingStatus.trainingComplete === true) {
        setVideosCompleted(modules.map((video) => video.ID));
      }
    }
  }, [getTrainingModuleSuccess, getProductTrainingStatusSuccess, modules]);

  const { mutate: setProductTrainingMutation, isPending: setProductTrainingMutationLoading } =
    useMutation({
      mutationFn: () => SetProductTraining({ Training: trainingModule })(),
      onSuccess: (data) => {
        if (data.success === true) {
          setSnackbarDetails((prev) => ({ ...prev, status: 'success', message: 'Success!', open: true }));
        } else if (data.success === false) {
          setSnackbarDetails((prev) => ({ ...prev, status: 'error', message: 'Something went wrong', open: true }));
        }
        queryClient.invalidateQueries({ queryKey: ['getProductTrainingStatus'] });
      },
    });

  const { currentVideo, nextVideo, hasNext, selectVideo } = useVideoModulePlaylist(modules);

  const toggleDrawer = () => {
    setDrawerOpen((prev) => !prev);
  };

  const handleCompleteTraining = () => {
    if (modulesCompletedCount !== modulesCount) {
      return;
    }

    if (productTrainingStatus.trainingCompleted === true) {
      return;
    }

    setProductTrainingMutation({ Training: trainingModule });
  };

  const handleSidebarNav = (videoID, index) => {
    if (!videosCompleted.includes(videoID)) {
      return;
    }
    selectVideo(index);
  };

  const handleVideoCompleted = (videoID) => {
    if (videosCompleted.includes(videoID)) {
      return;
    }
    setVideosCompleted((prev) => [...prev, videoID]);
    setModulesCompletedCount((prev) => prev + 1);
  };

  if (hasProductAccessLoading) {
    return <TrainingModuleLoadingState />;
  }

  if (!accessDenied && hasProductAccessError) {
    return <TrainingModuleError onRetry={refetchAccess} />;
  }

  if (accessDenied) {
    return (
      <Box sx={centeredMessageSx}>
        <Typography variant="h5">Access Denied</Typography>
        <Typography variant="body1" color="text.secondary">
          You do not have access to this training. Please contact Baltimore Life Agent Relations at
          (877) 533-2468 for more information.
        </Typography>
      </Box>
    );
  }

  if (getProductTrainingStatusLoading || getTrainingModuleLoading) {
    return <TrainingModuleLoadingState />;
  }

  if (getProductTrainingStatusError || getTrainingModuleError) {
    return (
      <TrainingModuleError
        onRetry={() => {
          refetchStatus();
          refetchModule();
        }}
      />
    );
  }

  return (
    <>
      {getProductTrainingStatusSuccess && getTrainingModuleSuccess && (
        <Box sx={{ display: 'flex', position: 'relative' }}>
          <Snackbar
            open={snackbarDetails.open}
            duration={3000}
            message={snackbarDetails.message}
            status={snackbarDetails.status}
            onClose={() =>
              setSnackbarDetails({
                ...snackbarDetails,
                open: false,
              })
            }
          />
          {drawerOpen ? (
            <MobileDrawer in={drawerOpen} timeout="auto" unmountOnExit>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  minHeight: '4rem',
                  borderBottom: '1px solid grey.200',
                }}
              >
                <IconButton onClick={toggleDrawer} sx={{ marginRight: '.875rem' }}>
                  <ChevronLeftIcon />
                </IconButton>
              </Box>
              <Box>
                <List sx={{ height: '100%' }}>
                  {trainingModuleData?.map((module) => (
                    <ListItem
                      sx={{
                        height: '100%',
                        flexDirection: 'column',
                        padding: '0',
                      }}
                      key={module.id}
                    >
                      <ListItemText
                        sx={{
                          flex: 'unset',
                          textAlign: 'left',
                          width: '100%',
                          padding: '0 .5rem',
                          '& .MuiListItemText-primary': {
                            fontSize: '1.3rem',
                          },
                        }}
                        primary={module.title}
                      />
                      <Divider />
                      {modules &&
                        modules.length > 1 &&
                        modules.map((video, index) => (
                          <SidebarButton
                            key={video.ID}
                            video={video}
                            index={index}
                            currentVideo={currentVideo}
                            videosCompleted={videosCompleted}
                            handleSidebarNav={handleSidebarNav}
                            productTrainingStatus={productTrainingStatus}
                          />
                        ))}
                    </ListItem>
                  ))}
                </List>
              </Box>
            </MobileDrawer>
          ) : (
            ''
          )}
          <Box sx={{ flex: '1' }}>
            {productTrainingStatus.trainingComplete && (
              <Box
                sx={{
                  padding: '.5rem 0',
                  display: 'flex',
                }}
              >
                <Alert icon={<CheckIcon size="small" />} severity="success">
                  Training completed on:{' '}
                  {convertIsoToReadableDate(productTrainingStatus.completedDate, 'en-us')}
                </Alert>
              </Box>
            )}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                minHeight: '4rem',
                borderBottom: '1px solid grey.200',
              }}
            >
              {isMedium || isMobile ? (
                <IconButton onClick={toggleDrawer}>
                  <MenuIcon />
                </IconButton>
              ) : (
                ''
              )}
              <Typography sx={{ paddingLeft: isMobile || isMedium ? '1rem' : '' }} variant="h4">
                Training Module
              </Typography>
            </Box>
            <Box display="flex" sx={{ minHeight: '500px' }}>
              <Drawer variant="permanent" sx={{ zIndex: '1000' }}>
                <Box>
                  <List sx={{ height: '100%' }}>
                    {trainingModuleData?.map((module) => (
                      <ListItem
                        sx={{
                          height: '100%',
                          flexDirection: 'column',
                          padding: '0',
                        }}
                        key={module.id}
                      >
                        <ListItemText
                          sx={{
                            flex: 'unset',
                            textAlign: 'left',
                            width: '100%',
                            padding: '0 .5rem',
                            '& .MuiListItemText-primary': {
                              fontSize: '1.3rem',
                            },
                          }}
                          primary={module.title}
                        />
                        <Divider />
                        {modules &&
                          modules.length > 1 &&
                          modules.map((video, index) => (
                            <SidebarButton
                              key={video.ID}
                              video={video}
                              index={index}
                              currentVideo={currentVideo}
                              videosCompleted={videosCompleted}
                              handleSidebarNav={handleSidebarNav}
                              productTrainingStatus={productTrainingStatus}
                            />
                          ))}
                      </ListItem>
                    ))}
                  </List>
                </Box>
              </Drawer>
              <Box component="main" sx={{ flexGrow: 1, bgcolor: 'background.default', p: 3 }}>
                <Box sx={{ width: '100%', height: '100%' }}>
                  {productTrainingStatus.trainingComplete === false && (
                    <Alert icon={<WarningAmberIcon size="small" />} severity="warning">
                      You must press the <strong>Complete Training</strong> button below to finish
                      training.
                    </Alert>
                  )}
                  <TrainingModuleVideoPlayer
                    trainingCompleted={productTrainingStatus.trainingComplete}
                    handleVideoCompleted={handleVideoCompleted}
                    nextVideo={nextVideo}
                    currentVideo={currentVideo}
                    hasNext={hasNext}
                  />
                  <Box
                    sx={{
                      padding: '.5rem 0',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'flex-end',
                      flexDirection: 'column',
                      gap: '.5rem',
                    }}
                  >
                    {hasNext ? (
                      <Button
                        variant="contained"
                        onClick={nextVideo}
                        disabled={videosCompleted <= 0}
                      >
                        Next Video
                      </Button>
                    ) : setProductTrainingMutationLoading ? (
                      <LoadingButton
                        text="Complete Training"
                        loading={setProductTrainingMutationLoading}
                        variant="contained"
                      />
                    ) : productTrainingStatus.trainingComplete === true ? (
                      ''
                    ) : (
                      <Button
                        variant="contained"
                        disabled={videosCompleted.length !== modulesCount}
                        onClick={handleCompleteTraining}
                      >
                        Complete Training
                      </Button>
                    )}
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: '1rem',
                      }}
                    >
                      <LinearProgress
                        sx={{ width: '5rem' }}
                        variant="determinate"
                        value={(videosCompleted.length / modulesCount) * 100}
                      />
                      <Typography
                        sx={{ color: modulesCompletedCount > 0 ? 'common.black' : 'grey.400' }}
                        variant="body2"
                      >
                        {videosCompleted.length} / {modulesCount}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      )}
    </>
  );
};
